/**
 * Third-person camera.
 *
 * Sits behind and above the player and eases toward its target rather than snapping,
 * so turning doesn't whip the view. Interpolation happens at render rate, not
 * simulation rate, so the smoothing is expressed as a per-second rate and converted
 * with an exponential so it behaves identically at 30fps and 144fps.
 */
import * as THREE from 'three'

const DISTANCE = 6.4
const HEIGHT = 2.3
/** Look slightly above the player's centre, at roughly head height. */
const LOOK_AT_HEIGHT = 0.85
/** Higher is snappier. 8/s settles in about a third of a second. */
const FOLLOW_RATE = 8
/**
 * How far the camera drifts back when the player is moving fast. A camera welded at a
 * fixed distance gives no sense of speed at all; letting it fall behind and catch up is
 * most of what makes running feel like running.
 */
const SPEED_PULLBACK = 0.55
/** How much the view rolls when banking into a turn. */
const ROLL_MAX = 0.05
/**
 * Never let the camera sink below this. Looking fully up swings the rig underneath the
 * player, which put the camera below the ground plane and rendered the world from
 * inside the terrain.
 */
const MIN_CAMERA_HEIGHT = 0.65
/**
 * How far the aim point swings vertically with pitch. Height alone cannot express
 * looking up: the camera hits the ground floor at about -0.5 rad and every steeper
 * angle then renders identically. Swinging the look target instead keeps the full
 * range usable no matter where the camera itself ends up.
 */
const LOOK_SWING = 5.5

export class ThirdPersonCamera {
  readonly camera: THREE.PerspectiveCamera
  /** 0..1 — how far into the soaring overview the camera is. */
  private soar = 0
  /** Eased camera pull-back from speed, and roll from leaning. */
  private pull = 0
  private roll = 0
  private readonly desired = new THREE.Vector3()
  private readonly lookTarget = new THREE.Vector3()
  private initialised = false

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 500)
  }

  /**
   * `dt` here is real frame time, not the fixed simulation step. `pitch` swings the
   * camera up and down around the player — Kate asked for "up up down" looking.
   */
  /** Blend toward the soaring overview: further back, higher, and a wider view. */
  setSoar(amount: number): void {
    this.soar = amount
    const fov = 60 + amount * 22
    if (Math.abs(this.camera.fov - fov) > 0.01) {
      this.camera.fov = fov
      this.camera.updateProjectionMatrix()
    }
  }

  /**
   * @param bodyScale How big this animal is against a human, used to pull the camera
   *   in and down for something small. A chameleon framed from a human's 2.3m looked
   *   like a beetle being watched from a chair; the point of being a small animal is
   *   that the world is large around you.
   */
  follow(target: THREE.Vector3, yaw: number, dt: number, pitch = 0,
         speed = 0, lean = 0, bodyScale = 1): void {
    // Square-rooted, so a chameleon at a fifth of human height does not end up with the
    // camera inside its own back. Clamped so nothing gets a telescope or a microscope.
    const frame = Math.min(1.4, Math.max(0.5, Math.sqrt(bodyScale)))
    // Pitch shortens the horizontal reach and raises the camera, orbiting the player.
    const cos = Math.cos(pitch)
    // Ease the pull-back so it builds with speed rather than snapping between states.
    const wantPull = Math.min(1, speed / 8) * SPEED_PULLBACK
    this.pull += (wantPull - this.pull) * Math.min(1, dt * 2.5)
    const reach = DISTANCE * frame * (1 + this.soar * 1.6 + this.pull) * Math.max(0.25, cos)
    this.desired.set(
      target.x + Math.sin(yaw) * reach,
      Math.max(MIN_CAMERA_HEIGHT * frame,
               target.y + HEIGHT * frame + this.soar * 9 + Math.sin(pitch) * DISTANCE * frame),
      target.z + Math.cos(yaw) * reach,
    )

    if (!this.initialised) {
      this.camera.position.copy(this.desired)
      this.initialised = true
    } else {
      // Frame-rate-independent exponential smoothing.
      const t = 1 - Math.exp(-FOLLOW_RATE * dt)
      this.camera.position.lerp(this.desired, t)
    }

    // Roll the view slightly into a turn, matching the body's lean.
    this.roll += (lean * (ROLL_MAX / 0.22) - this.roll) * Math.min(1, dt * 5)

    this.lookTarget.set(
      target.x,
      target.y + LOOK_AT_HEIGHT * frame - Math.sin(pitch) * LOOK_SWING * frame,
      target.z,
    )
    this.camera.lookAt(this.lookTarget)
    // lookAt rewrites the rotation, so the roll has to be re-applied after it.
    this.camera.rotateZ(this.roll)
  }
}
